import { Button, Form, InputNumber, message } from 'antd';
import * as React from 'react';
import { Selection } from '../enums';
import { submitPreferences } from '../PoolAPI';
import { DayCell } from './DayCell';

interface CalendarProps {
  firstDayOfWeek: number;
  daysInMonth: number;

  startingWorkDay: number;
  endingWorkDay: number;

  existingWorkers: number[];

  numDaysNeeded: number;
  maxBankDays: number;
  islandsAllowed?: boolean;

  allowedOffsiteDays: number;
  offsiteUnitOfTime: 'month' | 'week';

  initialBankDays?: number;
  initialDays?: string;
}

interface CalendarState {
  datesSelected: Selection[];
  bankDaysUsed: number;

  mouseDown: boolean;
  shiftDown: boolean;
  selectionOnMove: Selection;
}

function daysToString(days: number[]): string {
  const stringDays = days.map((value: number) => {
    if (value % 10 === 1 && value !== 11) {
      return value + 'st';
    } else if (value % 10 === 2 && value !== 12) {
      return value + 'nd';
    } else if (value % 10 === 3 && value !== 13) {
      return value + 'rd';
    }

    return value + 'th';
  });
  if (stringDays.length === 1) {
    return stringDays[0];
  } else if (stringDays.length === 2) {
    return stringDays[0] + ' and ' + stringDays[1];
  }

  const prefix = stringDays.slice(0, stringDays.length - 1).join(', ');

  return prefix + ', and ' + stringDays[stringDays.length - 1];
}

export class Calendar extends React.Component<CalendarProps, CalendarState> {
  shiftDownListener: (_: KeyboardEvent) => void;
  shiftUpListener: (_: KeyboardEvent) => void;

  constructor(props: CalendarProps) {
    super(props);

    let newSelection = this.generateHelpfulSelection();
    if (this.props.initialDays !== undefined) {
      const components = this.props.initialDays
        .slice(1, this.props.initialDays.length - 1)
        .split(',');
      newSelection = components.map(
        (s: string) => parseInt(s, 10) as Selection
      );
    }

    this.state = {
      datesSelected: newSelection,
      bankDaysUsed:
        this.props.initialBankDays === undefined
          ? 0
          : this.props.initialBankDays,
      mouseDown: false,
      shiftDown: false,
      selectionOnMove: Selection.Selected,
    };

    this.shiftDownListener = (e: KeyboardEvent) => {
      if (e.which === 16) {
        this.setState({
          shiftDown: true,
          selectionOnMove: Selection.Offsite,
        });
      }
    };
    this.shiftUpListener = (e: KeyboardEvent) => {
      if (e.which === 16) {
        this.setState({
          shiftDown: false,
          selectionOnMove: Selection.Selected,
        });
      }
    };
  }

  componentDidMount() {
    window.addEventListener('keydown', this.shiftDownListener);
    window.addEventListener('keyup', this.shiftUpListener);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.shiftDownListener);
    window.removeEventListener('keyup', this.shiftUpListener);
  }

  render() {
    const rows = [];
    let daysGenerated = 0;
    while (daysGenerated < this.props.daysInMonth) {
      if (daysGenerated === 0) {
        const firstRow = [];
        for (let i = 0; i < this.props.firstDayOfWeek % 7; i++) {
          firstRow.push(<td key={'first-row-empty-' + i} />);
        }
        for (let i = this.props.firstDayOfWeek % 7; i < 7; i++) {
          firstRow.push(
            <DayCell
              key={daysGenerated + 1}
              day={daysGenerated + 1}
              disabled={
                daysGenerated + 1 < this.props.startingWorkDay ||
                daysGenerated + 1 > this.props.endingWorkDay
              }
              selection={this.state.datesSelected[daysGenerated]}
              requestSelected={this.setterForDay(daysGenerated + 1)}
              numWorkers={this.props.existingWorkers[daysGenerated]}
              changeSelectedOnMove={this.state.mouseDown}
              selectOnMoveStatus={this.state.selectionOnMove}
            />
          );
          daysGenerated += 1;
        }
        rows.push(<tr key="first-row">{firstRow}</tr>);
      }

      const newRow = [];
      for (let i = 0; i < 7; i++) {
        if (daysGenerated < this.props.daysInMonth) {
          newRow.push(
            <DayCell
              key={daysGenerated + 1}
              day={daysGenerated + 1}
              disabled={
                daysGenerated + 1 < this.props.startingWorkDay ||
                daysGenerated + 1 > this.props.endingWorkDay
              }
              selection={this.state.datesSelected[daysGenerated]}
              requestSelected={this.setterForDay(daysGenerated + 1)}
              numWorkers={this.props.existingWorkers[daysGenerated]}
              changeSelectedOnMove={this.state.mouseDown}
              selectOnMoveStatus={this.state.selectionOnMove}
            />
          );
          daysGenerated += 1;
        } else {
          newRow.push(<td key={'last-row-empty-' + i} />);
        }
      }
      rows.push(<tr key={'row-at-' + rows.length}>{newRow}</tr>);
    }

    const poolDaysSatisfied = this.getPoolDaysSatisfied();
    const islands = this.findIslands();
    const offsiteDays = this.getOffsiteDays();

    const errorStrings = [];
    if (islands.length > 0) {
      errorStrings.push(
        'There ' +
          (islands.length === 1 ? 'is ' : 'are ') +
          islands.length +
          ' island day' +
          (islands.length === 1 ? '' : 's') +
          ' on the ' +
          daysToString(islands) +
          '.'
      );
    }
    if (!this.offsiteDaysSatisfiesLimits(offsiteDays)) {
      errorStrings.push(
        'You are allowed at most ' +
          this.props.allowedOffsiteDays +
          ' offsite day' +
          (this.props.allowedOffsiteDays === 1 ? '' : 's') +
          ' per ' +
          this.props.offsiteUnitOfTime +
          '.'
      );
    }

    return (
      <span>
        <Form layout="inline" className="space-out">
          <Form.Item label="# of bank days">
            <InputNumber
              min={0}
              max={this.props.maxBankDays}
              value={this.state.bankDaysUsed}
              disabled={this.props.maxBankDays === 0}
              onChange={(input: number | string) => {
                let days = parseInt('' + input, 10);
                if (isNaN(days) || days < 0) {
                  days = 0;
                }
                if (days > this.props.maxBankDays) {
                  days = this.props.maxBankDays;
                }
                this.setState({
                  bankDaysUsed: days,
                });
              }}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.currentTarget.blur();
                this.setState({
                  datesSelected: this.generateHelpfulSelection(),
                });
              }}
            >
              Pick Helpful Days
            </Button>
          </Form.Item>
        </Form>
        <table className="calendar-table">
          <thead>
            <tr>
              <th>Sun</th>
              <th>Mon</th>
              <th>Tue</th>
              <th>Wed</th>
              <th>Thu</th>
              <th>Fri</th>
              <th>Sat</th>
            </tr>
          </thead>
          <tbody
            onMouseDownCapture={(e: React.MouseEvent<HTMLElement>) =>
              this.handleMouseDown(e)
            }
          >
            {rows}
          </tbody>
        </table>

        <p className="center">
          <strong>
            {poolDaysSatisfied}/{this.props.numDaysNeeded}
          </strong>
          &nbsp;days selected, including bank days
        </p>
        <p className="center">&nbsp;{errorStrings.join(' ')}</p>
        <div className="center">
          <Button
            type="primary"
            size="large"
            disabled={!this.canSubmit()}
            onClick={() => this.submitResponse()}
          >
            Submit
          </Button>
        </div>
      </span>
    );
  }

  private generateHelpfulSelection(): Selection[] {
    const newSelection = [];
    for (let i = 0; i < this.props.daysInMonth; i++) {
      newSelection.push(Selection.None);
    }

    const startIndex = this.props.startingWorkDay - 1;
    const endIndex = this.props.endingWorkDay - 1;
    let argMaxIndex: [number, number][] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      argMaxIndex.push([this.props.existingWorkers[i], i]);
    }
    argMaxIndex.sort((a: [number, number], b: [number, number]) => {
      if (b[0] > a[0]) {
        return 1;
      } else if (b[0] < a[0]) {
        return -1;
      } else {
        return 2 * Math.floor(Math.random() * 2) - 1;
      }
    });

    let numSatisfied = 0;
    if (this.state !== undefined) {
      numSatisfied = this.state.bankDaysUsed;
    }
    while (numSatisfied < this.props.numDaysNeeded) {
      if (numSatisfied === this.props.numDaysNeeded - 1) {
        while (argMaxIndex.length > 0) {
          const candidate = argMaxIndex.pop();
          if (candidate === undefined) {
            break;
          }
          const candIndex = candidate[1];
          if (newSelection[candIndex] !== Selection.None) {
            continue;
          }

          if (
            (candIndex === startIndex &&
              newSelection[candIndex + 1] !== Selection.None) ||
            (candIndex === endIndex &&
              newSelection[candIndex - 1] !== Selection.None) ||
            (candIndex !== startIndex &&
              candIndex !== endIndex &&
              (newSelection[candIndex - 1] !== Selection.None ||
                newSelection[candIndex + 1] !== Selection.None))
          ) {
            newSelection[candIndex] = Selection.Selected;
            numSatisfied += 1;
            break;
          }
        }
        break;
      }
      const next = argMaxIndex.pop();
      if (next === undefined) {
        break;
      }

      const index = next[1];
      if (newSelection[index] !== Selection.None) {
        continue;
      }
      newSelection[index] = Selection.Selected;
      numSatisfied += 1;

      if (
        (index === startIndex || newSelection[index - 1] === Selection.None) &&
        (index === endIndex || newSelection[index + 1] === Selection.None)
      ) {
        if (index === startIndex) {
          newSelection[startIndex + 1] = Selection.Selected;
        } else if (index === endIndex) {
          newSelection[endIndex - 1] = Selection.Selected;
        } else {
          if (
            this.props.existingWorkers[index - 1] <
            this.props.existingWorkers[index + 1]
          ) {
            newSelection[index - 1] = Selection.Selected;
          } else if (
            this.props.existingWorkers[index - 1] >
            this.props.existingWorkers[index + 1]
          ) {
            newSelection[index + 1] = Selection.Selected;
          } else {
            if (Math.random() > 0.5) {
              newSelection[index - 1] = Selection.Selected;
            } else {
              newSelection[index + 1] = Selection.Selected;
            }
          }
        }

        numSatisfied += 1;
      }
    }

    return newSelection;
  }

  private handleMouseDown(e: React.MouseEvent<HTMLElement>) {
    const target = e.target as Element;
    const shouldSelect = !target.classList.contains('selected')
      ? Selection.Selected
      : Selection.None;

    this.setState({
      mouseDown: true,
      selectionOnMove: this.state.shiftDown ? Selection.Offsite : shouldSelect,
    });

    const upListener = () => {
      this.setState({
        mouseDown: false,
      });
      window.removeEventListener('mouseup', upListener);
    };

    window.addEventListener('mouseup', upListener);
    e.preventDefault();
  }

  private setterForDay(day: number) {
    return (select: Selection) => {
      if (day < this.props.startingWorkDay || day > this.props.endingWorkDay) {
        return;
      }

      const newSelection = [];
      for (let i = 0; i < this.state.datesSelected.length; i++) {
        if (i !== day - 1) {
          newSelection[i] = this.state.datesSelected[i];
        } else {
          newSelection[i] = select;
        }
      }

      this.setState({
        datesSelected: newSelection,
      });
    };
  }

  private canSubmit(): boolean {
    const offsiteDays = this.getOffsiteDays();

    return (
      this.getPoolDaysSatisfied() === this.props.numDaysNeeded &&
      this.findIslands().length === 0 &&
      this.offsiteDaysSatisfiesLimits(offsiteDays)
    );
  }

  private async submitResponse() {
    const r = await submitPreferences(
      this.state.datesSelected,
      this.state.bankDaysUsed
    );
    if (r.status === 200) {
      message.success(
        'Submitted! You can continue to adjust your choices until the deadline.'
      );
    } else if (r.status >= 400) {
      const text = await r.text();
      message.error('Error: ' + text);
    }
  }

  private getPoolDaysSatisfied(): number {
    return (
      this.state.datesSelected.reduce(
        (count: number, currentValue: Selection) => {
          if (currentValue !== Selection.None) {
            count += 1;
          }
          return count;
        },
        0
      ) + this.state.bankDaysUsed
    );
  }

  private getOffsiteDays(): number[] {
    return this.state.datesSelected.reduce(
      (offsiteDays: number[], currentValue: Selection, index: number) => {
        if (currentValue === Selection.Offsite) {
          offsiteDays.push(index + 1);
        }
        return offsiteDays;
      },
      []
    );
  }

  private offsiteDaysSatisfiesLimits(offsiteDays: number[]): boolean {
    if (
      this.props.offsiteUnitOfTime === 'month' &&
      offsiteDays.length > this.props.allowedOffsiteDays
    ) {
      return false;
    }

    if (this.props.offsiteUnitOfTime === 'week' && offsiteDays.length > 0) {
      let index = 0;

      for (
        let i = 7 - this.props.firstDayOfWeek;
        i < this.props.daysInMonth + 7;
        i += 7
      ) {
        let numInWeek = 0;
        while (index < offsiteDays.length && offsiteDays[index] <= i) {
          numInWeek += 1;
          index += 1;
        }

        if (numInWeek > this.props.allowedOffsiteDays) {
          return false;
        }
      }
    }

    return true;
  }

  private findIslands(): number[] {
    if (this.props.islandsAllowed !== undefined && this.props.islandsAllowed) {
      return [];
    }

    return this.state.datesSelected.reduce(
      (islands: number[], value: Selection, index: number) => {
        if (value !== Selection.None && this.state.datesSelected.length > 1) {
          if (
            (index === 0 && !this.state.datesSelected[index + 1]) ||
            (index === this.state.datesSelected.length - 1 &&
              !this.state.datesSelected[index - 1]) ||
            (!this.state.datesSelected[index - 1] &&
              !this.state.datesSelected[index + 1])
          ) {
            islands.push(index + 1);
          }
        }
        return islands;
      },
      []
    );
  }
}

export default Calendar;



// WEBPACK FOOTER //
// ./src/components/Calendar.tsx